import { t } from 'ttag'
import { Route, Switch, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'

import { api } from '@h2/api'
import { NavigationToggle } from '@h2/common/components/navigationToggle'
import { UserSettingsIcon, GroupsIcon, EditIcon, TrashIcon } from '@h2/components/icons'
import { PB97T } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { managerQuota } from '@main/actions/admin/adminUserActions'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { Button } from '@h2/components/buttons/button'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'

import { Members } from './members'
import { Sharing } from './sharing'
import { EditGroup } from './edit'

import { ActionsRow } from '../../commonComponents/actionsRow'

const NavWrap = styled.div`
  padding: 10px;
  border-radius: 25px;
  background-color: ${PB97T};
  margin-bottom: 25px;
`
const GroupDetail = ({ selectedRecord }) => {
  const { groupId } = useParams()
  const dispatch = useDispatch()
  const navigator = useNavigator()
  const queryClient = useQueryClient()
  const { mutate: removeGroup, isLoading } = useMutation(
    groupToRemove => {
      return api.fetch(`/admin/users/groups/remove`, {
        method: 'POST',
        version: 'v1',
        body: { groupId: groupToRemove }
      })
    },
    {
      onSuccess: async () => {
        dispatch(managerQuota())
        await queryClient.refetchQueries(['groups'], { refetchType: 'all' })
        navigator('/management/groups')
      },
      onError: () => {
        dispatch(showUserFeedback(t`Could not remove group`, 'error'))
      }
    }
  )
  const routes = [
    {
      path: '/management/groups/:groupId',
      exact: true,
      render(props) {
        return <Members group={selectedRecord} {...props} />
      }
    },
    {
      path: '/management/groups/:groupId/sharing',
      exact: true,
      render(props) {
        return <Sharing group={selectedRecord} {...props} />
      }
    },
    {
      path: '/management/groups/:groupId/edit',
      exact: true,
      render(props) {
        return <EditGroup group={selectedRecord} {...props} />
      }
    }
  ]

  if (!selectedRecord) {
    return null
  }

  return (
    <div>
      <ActionsRow>
        <ActionsRow.Actions>
          <Button
            display="red"
            align="right"
            variant="outlined"
            icon={TrashIcon}
            isLoading={isLoading}
            disabled={selectedRecord.defaultFor}
            onClick={() => {
              if (
                confirm(
                  t`Are you sure you wish to remove the group ${selectedRecord.name}?`
                )
              ) {
                removeGroup(selectedRecord.id)
              }
            }}>
            {t`Delete Group`}
          </Button>
        </ActionsRow.Actions>
      </ActionsRow>
      <Heading level="3">{selectedRecord.name}</Heading>
      <Text size="medium">
        <Text size="medium" as="span" weight="bold">{t`Created: `}</Text>{' '}
        {new Date(selectedRecord.created).toLocaleDateString()}
      </Text>
      <NavWrap>
        <NavigationToggle>
          <NavigationToggle.Item
            href={`/management/groups/${groupId}/edit`}
            text={t`Edit`}
            icon={EditIcon}
          />
          <NavigationToggle.Item
            href={`/management/groups/${groupId}`}
            text={t`Members`}
            exactMatch={true}
            icon={UserSettingsIcon}
          />
          <NavigationToggle.Item
            href={`/management/groups/${groupId}/sharing`}
            text={t`Sharing`}
            icon={GroupsIcon}
          />
        </NavigationToggle>
      </NavWrap>
      <Switch>
        {routes.map((route, idx) => (
          <Route
            key={idx}
            {...route}
            render={props => {
              return route.render({ ...props })
            }}
          />
        ))}
      </Switch>
    </div>
  )
}

export { GroupDetail }
